import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { salesApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

type Tab = 'statements' | 'rules';

/** FR-SALES-06 — Commissions */
export function CommissionsPage() {
  const [tab, setTab] = useState<Tab>('statements');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'statements', label: 'Statements' },
    { key: 'rules',      label: 'Rules' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Commerce"
        title="Commissions"
        subtitle="Track sales-rep commissions by period. Configure rules per product, territory, or tier."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-pill transition-all ${
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'statements' && <StatementsTab />}
      {tab === 'rules' && <RulesTab />}
    </div>
  );
}

function StatementsTab() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCalc, setShowCalc] = useState(false);

  const q = useQuery({
    queryKey: ['commission-statements'],
    queryFn: () => salesApi.listCommissionStatements({ limit: 200 }),
  });

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button className="btn-primary" onClick={() => setShowCalc(true)}>Calculate period</button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card overflow-x-auto">
            <table className="table">
              <thead><tr><th>Stmt #</th><th>Rep</th><th>Period</th><th className="text-right">Sales</th><th className="text-right">Commission</th><th>Status</th></tr></thead>
              <tbody>
                {!q.data?.length ? (
                  <tr><td colSpan={6}><EmptyState message="No statements." /></td></tr>
                ) : q.data.map((s: any) => (
                  <tr key={s.id} onClick={() => setSelected(s.id)}
                      className={`cursor-pointer ${selected === s.id ? 'bg-sage-50' : 'hover:bg-slate-50'}`}>
                    <td className="font-mono text-2xs">{s.statement_number}</td>
                    <td className="text-xs">{s.user_name}</td>
                    <td className="text-xs">
                      {new Date(s.period_from).toLocaleDateString()} → {new Date(s.period_to).toLocaleDateString()}
                    </td>
                    <td className="text-right font-mono">{fmtCurrency(parseFloat(s.total_sales))}</td>
                    <td className="text-right font-mono font-semibold">{fmtCurrency(parseFloat(s.total_commission))}</td>
                    <td><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card p-4">
            {!selected ? <EmptyState message="Select a statement." /> : (
              <StatementDetail statementId={selected}
                onChanged={() => qc.invalidateQueries({ queryKey: ['commission-statements'] })} />
            )}
          </div>
        </div>
      )}

      {showCalc && <CalculateModal onClose={() => setShowCalc(false)}
        onCalculated={() => { qc.invalidateQueries({ queryKey: ['commission-statements'] }); setShowCalc(false); }} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = {
    draft:    'bg-canvas-subtle text-ink-700 ring-ink-200',
    approved: 'bg-blue-50 text-blue-700 ring-blue-200',
    paid:     'bg-sage-50 text-sage-700 ring-sage-200',
    void:     'bg-red-50 text-red-700 ring-red-200',
  }[status] ?? 'bg-canvas-subtle text-ink-600 ring-ink-200';
  return <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>{status}</span>;
}

function StatementDetail({ statementId, onChanged }: { statementId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const [showVoid, setShowVoid] = useState(false);

  const q = useQuery({
    queryKey: ['commission-statement', statementId],
    queryFn: () => salesApi.getCommissionStatement(statementId),
  });

  const approveMut = useMutation({
    mutationFn: () => salesApi.approveCommissionStatement(statementId),
    onSuccess: () => { toast.success('Approved'); qc.invalidateQueries({ queryKey: ['commission-statement', statementId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const payMut = useMutation({
    mutationFn: () => salesApi.payCommissionStatement(statementId),
    onSuccess: () => { toast.success('Marked paid'); qc.invalidateQueries({ queryKey: ['commission-statement', statementId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const voidMut = useMutation({
    mutationFn: (reason: string) => salesApi.voidCommissionStatement(statementId, reason),
    onSuccess: () => {
      toast.success('Voided');
      qc.invalidateQueries({ queryKey: ['commission-statement', statementId] });
      onChanged();
      setShowVoid(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const s = q.data;

  return (
    <div>
      <div className="flex items-start justify-between mb-3 gap-2">
        <div>
          <div className="font-mono text-xs text-ink-500 mb-0.5">{s.statement_number}</div>
          <div className="font-semibold text-ink-900">{s.user_name}</div>
          <div className="text-xs text-ink-600">
            {new Date(s.period_from).toLocaleDateString()} → {new Date(s.period_to).toLocaleDateString()}
          </div>
        </div>
        <StatusBadge status={s.status} />
      </div>

      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        <div className="bg-canvas-subtle rounded-xl p-3">
          <div className="eyebrow text-ink-500 mb-1">Invoices</div>
          <div className="font-bold text-ink-900">{s.invoice_count}</div>
        </div>
        <div className="bg-canvas-subtle rounded-xl p-3">
          <div className="eyebrow text-ink-500 mb-1">Total sales</div>
          <div className="font-mono font-semibold text-ink-900">{fmtCurrency(parseFloat(s.total_sales))}</div>
        </div>
        <div className="bg-canvas-subtle rounded-xl p-3">
          <div className="eyebrow text-ink-500 mb-1">Commission</div>
          <div className="font-display font-bold text-lg text-sage-700 tabular-nums">{fmtCurrency(parseFloat(s.total_commission))}</div>
        </div>
      </div>

      <div className="text-2xs text-ink-500 mb-3 font-mono">
        Calculated {new Date(s.calculated_at).toLocaleString()} by {s.calculated_by_name}
        {s.approved_at && ` · approved ${new Date(s.approved_at).toLocaleDateString()} by ${s.approved_by_name}`}
        {s.paid_at && ` · paid ${new Date(s.paid_at).toLocaleDateString()} by ${s.paid_by_name}`}
      </div>

      <div className="eyebrow text-ink-500 mb-2">Line items</div>
      <div className="max-h-64 overflow-y-auto mb-3 border border-ink-100 rounded-xl overflow-hidden">
        <table className="table text-xs">
          <thead><tr><th>Invoice</th><th>Date</th><th className="text-right">Amount</th><th className="text-right">Rate</th><th className="text-right">Commission</th></tr></thead>
          <tbody>
            {s.lineItems.map((li: any) => (
              <tr key={li.id}>
                <td className="font-mono text-2xs">{li.invoice_number}</td>
                <td>{new Date(li.invoice_date).toLocaleDateString()}</td>
                <td className="text-right font-mono tabular-nums">{fmtCurrency(parseFloat(li.invoice_amount))}</td>
                <td className="text-right tabular-nums">{li.rate_applied}%</td>
                <td className="text-right font-mono tabular-nums font-semibold text-sage-700">{fmtCurrency(parseFloat(li.commission_amount))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 justify-end border-t border-ink-100 pt-3 flex-wrap">
        {s.status === 'draft' && (
          <>
            <button className="btn-secondary" disabled={voidMut.isPending}
              onClick={() => setShowVoid(true)}>Void</button>
            <button className="btn-primary" disabled={approveMut.isPending} onClick={() => approveMut.mutate()}>Approve</button>
          </>
        )}
        {s.status === 'approved' && (
          <>
            <button className="btn-secondary" disabled={voidMut.isPending}
              onClick={() => setShowVoid(true)}>Void</button>
            <button className="btn-primary" disabled={payMut.isPending} onClick={() => payMut.mutate()}>Mark paid</button>
          </>
        )}
      </div>

      {showVoid && (
        <Modal
          onClose={() => setShowVoid(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Sales · Commissions</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Void statement?</h2>
            <p className="text-sm text-ink-500 mt-1 leading-relaxed">
              Statement <span className="font-mono font-semibold text-ink-900">{s.statement_number}</span> will be voided. A reason is required.
            </p>
          </div>
          <div className="px-6 py-5">
            <VoidDialog
              loading={voidMut.isPending}
              onClose={() => setShowVoid(false)}
              onConfirm={(r) => voidMut.mutate(r)}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

function VoidDialog({ loading, onClose, onConfirm }: { loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <>
      <label className="label">Void reason <span className="text-danger">*</span></label>
      <textarea className="input mb-4" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Calculation error, period mismatch" />
      <div className="flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())}>
          {loading ? 'Voiding…' : 'Void statement'}
        </button>
      </div>
    </>
  );
}

function CalculateModal({ onClose, onCalculated }: { onClose: () => void; onCalculated: () => void }) {
  const firstOfMonth = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); })();
  const endOfMonth = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); })();

  const [periodFrom, setPeriodFrom] = useState(firstOfMonth);
  const [periodTo, setPeriodTo] = useState(endOfMonth);
  const [replaceExisting, setReplaceExisting] = useState(false);

  const mut = useMutation({
    mutationFn: () => salesApi.calculateCommissionStatements({
      periodFrom, periodTo, replaceExisting,
    }),
    onSuccess: (d) => {
      if (d.statementsCreated === 0) {
        toast(d.message ?? 'No qualifying invoices in period', { icon: 'ℹ️' });
      } else {
        toast.success(
          `${d.statementsCreated} statement${d.statementsCreated === 1 ? '' : 's'} created${d.statementsReplaced > 0 ? ` (${d.statementsReplaced} replaced)` : ''}. Total: ${fmtCurrency(d.totalCommission)}`,
          { duration: 6000 }
        );
      }
      onCalculated();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Sales · Commissions</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Calculate commissions</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">
          For each sales rep with invoices in this period, create a commission statement.
          Rules are matched per invoice; invoices without a matching rule contribute $0.
        </p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Period from</label>
            <input type="date" className="input" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">Period to</label>
            <input type="date" className="input" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
          </div>
        </div>
        <label className="flex items-start gap-2.5 text-sm text-ink-700 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={replaceExisting}
            onChange={(e) => setReplaceExisting(e.target.checked)} />
          <div>
            <div className="font-medium">Replace existing draft statements</div>
            <div className="text-2xs text-ink-500 mt-0.5">Won't affect approved or paid statements.</div>
          </div>
        </label>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Calculating…' : 'Calculate commissions'}
        </button>
      </div>
    </Modal>
  );
}

function RulesTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const q = useQuery({
    queryKey: ['commission-rules'],
    queryFn: () => salesApi.listCommissionRules({ status: 'active' }),
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New rule</button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead><tr><th>Name</th><th>Rep</th><th className="text-right">Rate</th><th>Constraints</th><th>Effective</th><th>Priority</th></tr></thead>
            <tbody>
              {!q.data?.length ? (
                <tr><td colSpan={6}><EmptyState message="No commission rules. Create a default rule first." /></td></tr>
              ) : q.data.map((r: any) => (
                <tr key={r.id}>
                  <td className="text-xs">{r.name}</td>
                  <td className="text-xs">{r.user_name ?? '(tenant default)'}</td>
                  <td className="text-right font-mono tabular-nums">{parseFloat(r.rate_percent).toFixed(1)}%</td>
                  <td className="text-xs text-ink-600">
                    {r.min_invoice_amount && `≥${fmtCurrency(parseFloat(r.min_invoice_amount))}`}
                    {r.min_invoice_amount && r.max_invoice_amount && ' '}
                    {r.max_invoice_amount && `<${fmtCurrency(parseFloat(r.max_invoice_amount))}`}
                    {r.category_name && ` · ${r.category_name}`}
                  </td>
                  <td className="text-xs">
                    {new Date(r.effective_from).toLocaleDateString()}
                    {r.effective_to && ` → ${new Date(r.effective_to).toLocaleDateString()}`}
                  </td>
                  <td className="text-right text-xs tabular-nums">{r.priority}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateRuleModal onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['commission-rules'] }); setShowCreate(false); }} />}
    </div>
  );
}

function CreateRuleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [userId, setUserId] = useState<string>('');
  const [name, setName] = useState('');
  const [ratePercent, setRatePercent] = useState(5);
  const [minAmt, setMinAmt] = useState<string>('');
  const [maxAmt, setMaxAmt] = useState<string>('');
  const [priority, setPriority] = useState(0);

  const mut = useMutation({
    mutationFn: () => salesApi.createCommissionRule({
      userId: userId || null,
      name, ratePercent,
      minInvoiceAmount: minAmt ? parseFloat(minAmt) : undefined,
      maxInvoiceAmount: maxAmt ? parseFloat(maxAmt) : undefined,
      priority,
    }),
    onSuccess: () => { toast.success('Rule created'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Sales · Commissions</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New commission rule</h2>
      </div>
      <div className="px-6 py-5 grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="label">Rule name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Tenant default / Senior rep rate" />
        </div>
        <div className="col-span-2">
          <label className="label">User ID <span className="font-normal text-ink-400 normal-case tracking-normal">(blank for tenant-wide default)</span></label>
          <input className="input font-mono text-xs" value={userId}
            onChange={(e) => setUserId(e.target.value)} placeholder="UUID or leave blank" />
          <div className="text-2xs text-ink-500 mt-1">Find user IDs in the Users admin page.</div>
        </div>
        <div>
          <label className="label">Rate %</label>
          <input type="number" min={0} max={100} step={0.1} className="input tabular-nums" value={ratePercent}
            onChange={(e) => setRatePercent(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Priority <span className="font-normal text-ink-400 normal-case tracking-normal">(higher wins)</span></label>
          <input type="number" min={0} max={999} className="input tabular-nums" value={priority}
            onChange={(e) => setPriority(parseInt(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Min invoice amount <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="number" min={0} step={1} className="input tabular-nums" value={minAmt}
            onChange={(e) => setMinAmt(e.target.value)} placeholder="no minimum" />
        </div>
        <div>
          <label className="label">Max invoice amount <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="number" min={0} step={1} className="input tabular-nums" value={maxAmt}
            onChange={(e) => setMaxAmt(e.target.value)} placeholder="no maximum" />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || ratePercent <= 0 || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create rule'}
        </button>
      </div>
    </Modal>
  );
}
